import { logger } from '@/lib/logger';

import { ConsentPlugin, ConsentCategory } from '../types';
import { AXEPTIO_CONFIG } from '../utils';

const AXEPTIO_VENDOR_MAP: Record<ConsentCategory, string> = {
  essential: 'essential',
  analytics: 'enhanced_analytics',
  marketing: 'marketing',
  logging: 'enhanced_errors',
};

type AxeptioConfig = {
  clientId: string;
  cookiesVersion: string;
  locale: string;
  tenantId: string;
};

export function createAxeptioPlugin(config: AxeptioConfig): ConsentPlugin {
  const injectScript = () => {
    if (document.getElementById('axeptio-sdk')) return;

    const script = document.createElement('script');
    script.src = 'https://static.axept.io/sdk.js';
    script.id = 'axeptio-sdk';
    script.async = true;
    document.head.appendChild(script);

    return new Promise<void>(resolve => {
      script.onload = () => resolve();
    });
  };

  return {
    async init() {
      if (typeof window === 'undefined') return;

      const getLocaleFromURL = () => {
        const pathParts = window.location.pathname.split('/').filter(Boolean);
        const urlLocale = pathParts[0];
        return /^[a-z]{2,4}$/i.test(urlLocale) ? urlLocale.toLowerCase() : 'en';
      };

      const actualLocale = getLocaleFromURL();
      const actualVersion = (() => {
        const siteConfig = AXEPTIO_CONFIG[config.tenantId];
        return (
          siteConfig?.[actualLocale] ||
          siteConfig?.['en'] ||
          siteConfig?.['default'] ||
          config.cookiesVersion
        );
      })();

      window.axeptioSettings = {
        clientId: config.clientId,
        cookiesVersion: actualVersion,
        locale: actualLocale,
      };

      await injectScript();
    },

    hasConsent(category: ConsentCategory) {
      const vendor = AXEPTIO_VENDOR_MAP[category];
      return !!window.axeptioSDK?.hasAcceptedVendor(vendor);
    },

    onChange(callback: () => void) {
      if (typeof window === 'undefined') return;
      window._axcb = window._axcb || [];
      window._axcb.push(sdk => {
        sdk.on(
          'cookies:complete',
          () => {
            callback();
          },
          { replay: true },
        );
        sdk.on('consent:saved', () => {
          callback();
        });
      });
    },

    setCookiesLanguage(languageCode: string) {
      const versions = AXEPTIO_CONFIG[config.tenantId] || {};
      const newVersion =
        versions[languageCode] || versions['en'] || Object.values(versions)[0];

      if (newVersion) {
        window._axcb = window._axcb || [];
        window._axcb.push(sdk => {
          if (sdk && typeof sdk.setCookiesVersion === 'function') {
            try {
              setTimeout(() => {
                sdk.setCookiesVersion(newVersion);
              }, 100);
            } catch (error) {
              logger.warn(
                String(error instanceof Error ? error.message : error),
              );
            }
          }
        });
      }
    },
  };
}
